import React, { useState } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { 
  Newspaper, 
  Download, 
  Mail, 
  ExternalLink, 
  Image, 
  FileText, 
  Palette, 
  Users, 
  Building2,
  Check,
  Copy,
  Phone,
  Globe,
  CheckCircle,
  Package,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

const PressPage = () => {
  const [copiedColor, setCopiedColor] = useState(null);

  const pressReleases = [
    { date: '2025-01-15', title: 'FundTheHope Reaches New Global Milestone', link: '#' },
    { date: '2025-01-01', title: 'Introducing New AI-Powered Campaign Recommendations', link: '#' },
    { date: '2024-12-15', title: 'FundTheHope Expands Global Reach', link: '#' },
    { date: '2024-11-20', title: 'FundTheHope Launches 0% Platform Fee Initiative', link: '#' },
    { date: '2024-10-01', title: 'New Mobile App Released for iOS and Android', link: '#' },
  ];

  const brandColors = [
    { name: 'Cyan Primary', hex: '#06B6D4', rgb: 'rgb(6, 182, 212)' },
    { name: 'Purple Accent', hex: '#8B5CF6', rgb: 'rgb(139, 92, 246)' },
    { name: 'Emerald Success', hex: '#10B981', rgb: 'rgb(16, 185, 129)' },
    { name: 'Dark Background', hex: '#0F172A', rgb: 'rgb(15, 23, 42)' },
    { name: 'Light Text', hex: '#F1F5F9', rgb: 'rgb(241, 245, 249)' },
  ];

  const logoAssets = [
    { name: 'Logo Full Color (PNG)', size: '2.4 MB', format: 'PNG', type: 'full' },
    { name: 'Logo Full Color (SVG)', size: '48 KB', format: 'SVG', type: 'full' },
    { name: 'Logo White (PNG)', size: '1.8 MB', format: 'PNG', type: 'white' },
    { name: 'Logo Dark (PNG)', size: '1.9 MB', format: 'PNG', type: 'dark' },
    { name: 'Logo Mark Only (PNG)', size: '890 KB', format: 'PNG', type: 'mark' },
    { name: 'Logo Mark Only (SVG)', size: '12 KB', format: 'SVG', type: 'mark' },
  ];

  const copyColor = (hex) => {
    navigator.clipboard.writeText(hex);
    setCopiedColor(hex);
    setTimeout(() => setCopiedColor(null), 2000);
  };

  const handleDownload = (assetType, assetName) => {
    // Generate downloadable content based on asset type
    let content, filename, mimeType;
    
    if (assetType === 'full' || assetType === 'white' || assetType === 'dark' || assetType === 'mark') {
      // Create SVG logo content
      const bgColor = assetType === 'white' ? '#1F2937' : 'none';
      const textColor = assetType === 'white' ? '#FFFFFF' : assetType === 'dark' ? '#1F2937' : '#10B981';
      const logoSvg = `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 300 80" width="300" height="80">
  ${bgColor !== 'none' ? `<rect width="300" height="80" fill="${bgColor}"/>` : ''}
  <defs>
    <linearGradient id="grad" x1="0%" y1="0%" x2="100%" y2="0%">
      <stop offset="0%" style="stop-color:#10B981;stop-opacity:1" />
      <stop offset="100%" style="stop-color:#14B8A6;stop-opacity:1" />
    </linearGradient>
  </defs>
  <circle cx="40" cy="40" r="28" fill="url(#grad)"/>
  <path d="M40 22 L40 48 M28 38 L40 50 L52 38" stroke="white" stroke-width="4" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
  ${assetType !== 'mark' ? `<text x="80" y="50" font-family="Arial, Helvetica, sans-serif" font-size="28" font-weight="bold" fill="${textColor}">FundTheHope</text>` : ''}
</svg>`;
      content = logoSvg;
      filename = `fundthehope-logo-${assetType}.svg`;
      mimeType = 'image/svg+xml';
    } else if (assetType === 'brand-guidelines') {
      content = generateBrandGuidelines();
      filename = 'FundTheHope-Brand-Guidelines.txt';
      mimeType = 'text/plain';
    } else if (assetType === 'fact-sheet') {
      content = generateFactSheet();
      filename = 'FundTheHope-Fact-Sheet.txt';
      mimeType = 'text/plain';
    } else if (assetType === 'executive-photos') {
      alert('Executive photos package coming soon. Please contact press@fundthehope.com for media assets.');
      return;
    } else {
      return;
    }
    
    // Create and trigger download
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const generateBrandGuidelines = () => {
    return `
================================================================================
                    FUNDTHEHOPE BRAND GUIDELINES
================================================================================

1. LOGO USAGE
-------------
• Always maintain clear space around the logo
• Minimum size: 120px wide for digital, 1 inch for print
• Do not stretch, rotate, or alter the logo
• Do not place the logo on busy backgrounds

2. PRIMARY COLORS
-----------------
Emerald Green (Primary): #10B981 - RGB(16, 185, 129)
Teal (Secondary): #14B8A6 - RGB(20, 184, 166)

3. SECONDARY COLORS
-------------------
Dark Background: #0F172A - RGB(15, 23, 42)
Light Gray: #F1F5F9 - RGB(241, 245, 249)
Muted Text: #94A3B8 - RGB(148, 163, 184)

4. TYPOGRAPHY
-------------
Headings: Inter Bold
Body: Inter Regular
Fallback: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif

5. VOICE & TONE
---------------
• Empowering and hopeful
• Clear and direct
• Inclusive and welcoming
• Trustworthy and transparent

6. DO'S AND DON'TS
------------------
DO:
✓ Use approved color combinations
✓ Maintain consistent spacing
✓ Follow accessibility guidelines
✓ Use high-quality images

DON'T:
✗ Use unapproved colors
✗ Alter the logo proportions
✗ Use low-resolution images
✗ Place text over busy backgrounds

================================================================================
              For questions, contact: press@fundthehope.com
================================================================================
`;
  };

  const generateFactSheet = () => {
    return `
================================================================================
                    FUNDTHEHOPE COMPANY FACT SHEET
================================================================================

COMPANY OVERVIEW
----------------
FundTheHope is a leading global crowdfunding platform that empowers individuals, 
families, and communities to raise funds for causes that matter most.

KEY STATISTICS (2025)
---------------------
• Platform Users: 2,000,000+
• Total Funds Raised: $2,500,000,000+
• Successful Campaigns: 500,000+
• Countries Served: 50+
• Average Campaign Success Rate: 78%
• Platform Fee: 0% (We don't take a cut)

CATEGORIES SERVED
-----------------
• Medical & Health Emergencies
• Education & Scholarships
• Community Projects
• Emergency & Crisis Relief
• Environmental Causes
• Creative & Arts Projects
• Sports & Recreation
• Memorial & Funeral Expenses

PLATFORM FEATURES
-----------------
• 0% Platform Fee - Standard payment processing fees apply
• AI-Powered Campaign Recommendations
• Trust Score System for Campaign Verification
• Identity Verification (KYC) for Campaign Creators
• Real-time Analytics Dashboard
• Multi-language Support
• Mobile-Optimized Experience

LEADERSHIP TEAM
---------------
• CEO & Co-Founder: [Leadership Name]
• CTO & Co-Founder: [Leadership Name]
• Head of Trust & Safety: [Leadership Name]
• Head of Community: [Leadership Name]

CONTACT INFORMATION
-------------------
Website: https://fundthehope.com
Press Inquiries: press@fundthehope.com
General Support: support@fundthehope.com
Social Media: @fundthehope

================================================================================
                Last Updated: January 2025
================================================================================
`;
  };

  const handleFullKitDownload = () => {
    // Create a text-based press kit with all information
    const pressKitContent = `
================================================================================
                         FUNDTHEHOPE PRESS KIT
================================================================================

ABOUT FUNDTHEHOPE
-----------------
FundTheHope is a global crowdfunding platform dedicated to empowering individuals 
and communities to raise funds for causes that matter. Since our founding, we have 
helped people raise funds for medical expenses, education, emergencies, 
and community projects.

COMPANY FACTS
-------------
• Founded: 2024
• Headquarters: Global (Remote-first company)
• Platform Users: Growing community
• Total Funds Raised: Supporting causes worldwide
• Countries Served: Global reach
• Platform Fee: 0%

BRAND COLORS
------------
Primary Emerald: #10B981 - rgb(16, 185, 129)
Teal Accent: #14B8A6 - rgb(20, 184, 166)
Dark Background: #0F172A - rgb(15, 23, 42)
Light Text: #F1F5F9 - rgb(241, 245, 249)

CONTACT INFORMATION
-------------------
Press Inquiries: press@fundthehope.com
General Support: support@fundthehope.com
Website: https://fundthehope.com

RECENT PRESS RELEASES
---------------------
• Jan 2025: FundTheHope Reaches New Global Milestone
• Jan 2025: Introducing New AI-Powered Campaign Recommendations
• Dec 2024: FundTheHope Expands Global Reach
• Nov 2024: FundTheHope Launches 0% Platform Fee Initiative
• Oct 2024: New Mobile App Released for iOS and Android

BRAND GUIDELINES
----------------
• Always use the official FundTheHope logo
• Maintain clear space around the logo equal to the height of the heart icon
• Do not alter, rotate, or distort the logo
• Use approved brand colors for consistency
• When in doubt, contact press@fundthehope.com

================================================================================
                    Thank you for your interest in FundTheHope!
================================================================================
`;
    
    const blob = new Blob([pressKitContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'FundTheHope-Press-Kit.txt';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Newspaper className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-foreground mb-4">Press & Media</h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              News, brand resources, and everything you need to tell the FundTheHope story
            </p>
          </div>

          {/* Quick Contact Cards */}
          <div className="grid md:grid-cols-2 gap-6 mb-12">
            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="pt-6">
                <Mail className="w-10 h-10 text-emerald-500 mb-4" />
                <h2 className="text-xl font-semibold text-foreground mb-3">Media Inquiries</h2>
                <p className="text-muted-foreground mb-4">
                  For press inquiries, interviews, and media partnerships, please contact our communications team.
                </p>
                <div className="space-y-2 text-sm">
                  <a href="mailto:press@fundthehope.com" className="flex items-center gap-2 text-emerald-500 hover:underline">
                    <Mail className="w-4 h-4" /> press@fundthehope.com
                  </a>
                  <span className="flex items-center gap-2 text-muted-foreground">
                    <Phone className="w-4 h-4" /> +1 (800) 555-HOPE
                  </span>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-emerald-500/10 to-teal-500/10 border-emerald-500/30 hover:shadow-lg transition-shadow">
              <CardContent className="pt-6">
                <Package className="w-10 h-10 text-teal-500 mb-4" />
                <h2 className="text-xl font-semibold text-foreground mb-3">Download Full Press Kit</h2>
                <p className="text-muted-foreground mb-4">
                  Get everything in one download: logos, brand guidelines, fact sheet, photos, and press releases.
                </p>
                <Button 
                  onClick={handleFullKitDownload}
                  className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
                >
                  <Download className="w-4 h-4 mr-2" /> Download Press Kit (ZIP)
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Press Kit Section */}
          <section className="mb-12">
            <div className="flex items-center gap-3 mb-6">
              <h2 className="text-2xl font-bold text-foreground">Press Kit</h2>
              <Badge className="bg-emerald-500/20 text-emerald-500">2025 Edition</Badge>
            </div>

            {/* Logo Downloads */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Image className="w-5 h-5 text-emerald-500" /> Logo Assets
                </CardTitle>
                <CardDescription>Download our logo in various formats and variations</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {logoAssets.map((asset, idx) => (
                    <div 
                      key={idx}
                      className="flex items-center justify-between p-4 bg-secondary/50 rounded-xl hover:bg-secondary transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                          <Image className="w-5 h-5 text-emerald-500" />
                        </div>
                        <div>
                          <p className="font-medium text-foreground text-sm">{asset.name}</p>
                          <p className="text-xs text-muted-foreground">{asset.size} • {asset.format}</p>
                        </div>
                      </div>
                      <Button 
                        size="sm" 
                        variant="ghost"
                        onClick={() => handleDownload(asset.type, asset.name)}
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Brand Colors */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Palette className="w-5 h-5 text-teal-500" /> Brand Colors
                </CardTitle>
                <CardDescription>Click on any color to copy its hex code</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  {brandColors.map((color, idx) => (
                    <button
                      key={idx}
                      onClick={() => copyColor(color.hex)}
                      className="group relative p-4 rounded-xl border border-border hover:border-emerald-500/50 transition-all"
                    >
                      <div 
                        className="w-full h-16 rounded-lg mb-3 shadow-sm"
                        style={{ backgroundColor: color.hex }}
                      />
                      <p className="font-medium text-foreground text-sm">{color.name}</p>
                      <p className="text-xs text-muted-foreground font-mono">{color.hex}</p>
                      {copiedColor === color.hex && (
                        <div className="absolute inset-0 bg-emerald-500/90 rounded-xl flex items-center justify-center">
                          <span className="text-white font-medium flex items-center gap-1">
                            <Check className="w-4 h-4" /> Copied!
                          </span>
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Additional Resources */}
            <div className="grid md:grid-cols-3 gap-4">
              <Card className="hover:border-emerald-500/50 transition-colors cursor-pointer" onClick={() => handleDownload('brand-guidelines')}>
                <CardContent className="pt-6 text-center">
                  <FileText className="w-10 h-10 text-emerald-500 mx-auto mb-3" />
                  <h3 className="font-semibold text-foreground mb-1">Brand Guidelines</h3>
                  <p className="text-sm text-muted-foreground mb-3">Complete style guide</p>
                  <Button variant="outline" size="sm">
                    <Download className="w-4 h-4 mr-2" /> Download
                  </Button>
                </CardContent>
              </Card>

              <Card className="hover:border-teal-500/50 transition-colors cursor-pointer" onClick={() => handleDownload('fact-sheet')}>
                <CardContent className="pt-6 text-center">
                  <Building2 className="w-10 h-10 text-teal-500 mx-auto mb-3" />
                  <h3 className="font-semibold text-foreground mb-1">Fact Sheet</h3>
                  <p className="text-sm text-muted-foreground mb-3">Company overview & stats</p>
                  <Button variant="outline" size="sm">
                    <Download className="w-4 h-4 mr-2" /> Download
                  </Button>
                </CardContent>
              </Card>

              <Card className="hover:border-emerald-500/50 transition-colors cursor-pointer" onClick={() => handleDownload('executive-photos')}>
                <CardContent className="pt-6 text-center">
                  <Users className="w-10 h-10 text-emerald-500 mx-auto mb-3" />
                  <h3 className="font-semibold text-foreground mb-1">Executive Photos</h3>
                  <p className="text-sm text-muted-foreground mb-3">Leadership team headshots</p>
                  <Button variant="outline" size="sm">
                    <Download className="w-4 h-4 mr-2" /> Download
                  </Button>
                </CardContent>
              </Card>
            </div>
          </section>

          {/* Press Releases */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-foreground mb-6">Press Releases</h2>
            <div className="space-y-4">
              {pressReleases.map((release, index) => (
                <Card key={index} className="hover:shadow-lg transition-shadow">
                  <CardContent className="py-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-sm text-muted-foreground">
                          {new Date(release.date).toLocaleDateString('en-US', { 
                            year: 'numeric', 
                            month: 'long', 
                            day: 'numeric' 
                          })}
                        </span>
                        <h3 className="text-lg font-semibold text-foreground">{release.title}</h3>
                      </div>
                      {release.link && release.link !== '#' ? (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          asChild
                        >
                          <a href={release.link} target="_blank" rel="noopener noreferrer">
                            <ExternalLink className="w-4 h-4 mr-2" /> Read
                          </a>
                        </Button>
                      ) : (
                        <span className="text-xs text-muted-foreground px-3 py-1 bg-muted/50 rounded-full">
                          Coming Soon
                        </span>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          {/* Stats Section */}
          <section className="mb-12">
            <Card className="bg-gradient-to-r from-emerald-500/10 via-teal-500/10 to-emerald-500/10">
              <CardContent className="py-8">
                <h2 className="text-xl font-semibold text-foreground text-center mb-8">FundTheHope at a Glance</h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
                  <div>
                    <div className="text-4xl font-bold text-emerald-500">Global</div>
                    <div className="text-muted-foreground text-sm">Impact</div>
                  </div>
                  <div>
                    <div className="text-4xl font-bold text-teal-500">Growing</div>
                    <div className="text-muted-foreground text-sm">Campaigns</div>
                  </div>
                  <div>
                    <div className="text-4xl font-bold text-emerald-500">Active</div>
                    <div className="text-muted-foreground text-sm">Community</div>
                  </div>
                  <div>
                    <div className="text-4xl font-bold text-amber-500">Worldwide</div>
                    <div className="text-muted-foreground text-sm">Reach</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </section>

          {/* About Section */}
          <section>
            <Card>
              <CardHeader>
                <CardTitle>About FundTheHope</CardTitle>
              </CardHeader>
              <CardContent className="prose prose-invert max-w-none">
                <p className="text-muted-foreground mb-4">
                  FundTheHope is a leading crowdfunding platform dedicated to making fundraising accessible, 
                  transparent, and impactful. Founded with the mission of connecting people who need help 
                  with those who want to give, we've helped fund campaigns across numerous countries worldwide.
                </p>
                <p className="text-muted-foreground mb-4">
                  What sets us apart is our <strong className="text-emerald-500">0% platform fee model</strong>. 
                  Unlike traditional platforms that take 5-10% of donations, we believe every dollar should 
                  reach those in need. We sustain our operations through optional tips from generous donors, 
                  ensuring maximum impact for every campaign.
                </p>
                <p className="text-muted-foreground">
                  Our platform features AI-powered recommendations, comprehensive verification systems, 
                  and industry-leading security measures to ensure a safe and trustworthy fundraising experience.
                </p>
              </CardContent>
            </Card>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default PressPage;
